/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectAccessDrawer.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-09-2024
 * @description: To give co-owner/editor/viewer access to the project/zones/ee component/software module
 */
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import customConsole from "../../config/customConsole";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  HandleApiActions,
} from "../../redux/actions/actionHandler";
import labCar from "../../redux/actions/labCar";
import userManagementApis from "../../redux/actions/userManagent";
import CloseIcon from "@mui/icons-material/Close";

import helper from "../../utils/helper";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import GrantAccessDrawer from "./project-access/access-provider/GrantAccessDrawer";

const ACCESS_TYPES = {
  PROJECT: "PROJECT",
  EE: "EE",
  SWM: "SWM",
  VARIANT: "VARIANT",
  VEHICLE_SYSTEM: "VS",
  VEHICLE_COMPONENT: "VC",
};

const EMPTY_ACCESS = {
  text1: "",
  text2: "",
  text3: "",
  architecture: "",
  project_details: [],
  variant_details: [],
  zone_details: [],
  ee_details: [],
  swm_details: [],
  vehicle_system_details: [],
  vehicle_component_details: [],
  owner_details: [],
  co_owner_details: [],
  edit_user_details: [],
  view_user_details: [],
};

const getAccessTitle = (accessType) => {
  switch (accessType) {
    case ACCESS_TYPES.PROJECT:
      return "Project Access";
    case ACCESS_TYPES.VARIANT:
      return "Variant Access";
    case ACCESS_TYPES.EE:
      return "EE Component Access";
    case ACCESS_TYPES.SWM:
      return "Software Module Access";
    case ACCESS_TYPES.VEHICLE_SYSTEM:
      return "Vehicle System Access";
    default:
      return "Vehicle Component Access";
  }
};

const getSupplierNames = (accessType, existingAccess) => {
  switch (accessType) {
    case ACCESS_TYPES.EE:
      return [existingAccess?.ee_details?.supplier_name];
    case ACCESS_TYPES.SWM:
      return [existingAccess?.swm_details?.supplier_name];
    case ACCESS_TYPES.VEHICLE_SYSTEM:
      return existingAccess?.vehicle_system_details?.suppliers || [];
    case ACCESS_TYPES.VEHICLE_COMPONENT:
      return existingAccess?.vehicle_component_details?.suppliers || [];
    default:
      return [];
  }
};

const resolveZoneDetails = (rawZoneDetails, zonePosition) => {
  if (Array.isArray(rawZoneDetails)) {
    return rawZoneDetails.find((z) => z?.position === zonePosition);
  }
  if (rawZoneDetails && rawZoneDetails.position === zonePosition) {
    return rawZoneDetails;
  }
  return null;
};

const buildUserLists = (data, usersInSystem) => {
  const createdBy = data?.created_by;
  const ownerDetails = data?.owner_details || [];
  const ownerList = usersInSystem?.filter(
    (user) => user?.user_id === createdBy,
  );
  const coOwnerList = usersInSystem?.filter(
    (user) =>
      user?.user_id !== createdBy &&
      ownerDetails?.some((coOwner) => coOwner?.user_id === user?.user_id),
  );
  const editorList = usersInSystem?.filter((user) =>
    data?.edit_user_details?.some(
      (editUser) => editUser?.user_id === user?.user_id,
    ),
  );
  const viewerList = usersInSystem?.filter((user) =>
    data?.view_user_details?.some(
      (viewUser) => viewUser?.user_id === user?.user_id,
    ),
  );

  return { ownerList, coOwnerList, editorList, viewerList };
};

const buildAccessDetails = (accessType, data, zoneDetails) => {
  const text1 = accessType === ACCESS_TYPES.EE ? data?.description : data?.name;
  const text2 =
    accessType === ACCESS_TYPES.PROJECT
      ? data?.architecture_name
      : data?.version;
  const text3 = accessType === ACCESS_TYPES.EE ? data?.description : "";

  return {
    text1: text1 || "",
    text2: text2 || "",
    text3: text3 || "",
    project_details: accessType === ACCESS_TYPES.PROJECT ? data : [],
    variant_details: accessType === ACCESS_TYPES.VARIANT ? data : null,
    zone_details: zoneDetails,
    ee_details: accessType === ACCESS_TYPES.EE ? data : null,
    swm_details: accessType === ACCESS_TYPES.SWM ? data : null,
    vehicle_system_details:
      accessType === ACCESS_TYPES.VEHICLE_SYSTEM ? data : null,
    vehicle_component_details:
      accessType === ACCESS_TYPES.VEHICLE_COMPONENT ? data : null,
  };
};

const buildRequestDetails = ({ accessType, data, showToast }) => {
  const withCommon = (apiDetails, params) => {
    const details = { ...apiDetails };
    delete details.action_type;
    return { show_toast: showToast, ...details, params };
  };

  switch (accessType) {
    case ACCESS_TYPES.PROJECT:
      return withCommon(labCar.get_project_details_pa, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
      });
    case ACCESS_TYPES.VARIANT:
      return withCommon(labCar.get_variant_details, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
        is_complete_details_required: true,
      });
    case ACCESS_TYPES.EE:
      return withCommon(labCar.get_ee_components_pa, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
        ee_id: data.ee_id,
      });
    case ACCESS_TYPES.SWM:
      return withCommon(labCar.get_sw_modules_pa, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
        ee_id: data.ee_id,
        swm_id: data.swm_id,
      });
    case ACCESS_TYPES.VEHICLE_SYSTEM:
      return withCommon(labCar.get_vehicle_systems_all, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
        vs_id: data.vs_id,
      });
    default:
      return withCommon(labCar.get_vehicle_components_all, {
        project_id: data.project_id,
        variant_id: data?.variant_id,
        vs_id: data.vs_id,
        vc_id: data.vc_id,
      });
  }
};

function AccessOverviewDrawer(props) {
  const [showGrant, setShowGrant] = useState(false);
  useEffect(() => {
    console.log("props.access", props?.data);
  }, []);

  const dispatch = useDispatch();

  /* useSelectors */
  const {
    project_status,

  } = useSelector((state) => state?.settingsReducer?.metaData, shallowEqual);

  const [existingAccess, setExistingAccess] = useState({
    name: "",
    version: "",
    description: "",
    architecture: "",
    owner_details: [],
    co_owner_details: [],
    edit_user_details: [],
    view_user_details: [],
  });

  const [enableNotification, setEnableNotification] = useState(true);
  const [usersInSystem, setUsersInSystem] = useState([]);
  const [supplierNamesList, setSupplierNamesList] = useState([]);
  const [entityAllUsersList, setEntityAllUsersList] = useState([]);

  const getUsersInSystem = async () => {
    const apiDetails = userManagementApis.get_users_list_pa;
    delete apiDetails.action_type;
    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params: {
          page_number: 1,
          page_size: 250,
        },
      }),
    );
    console.log("----get_users_list_pa---", resp.data);
    if (resp.error) {
      setUsersInSystem([]);
    } else {
      setUsersInSystem(resp.data);
    }
  };

  // Updates the supplier names list based on the selected access type.
  // Dependencies: existingAccess
  useEffect(() => {
    const filteredSupplierNamesList = getSupplierNames(
      props.data.access_type,
      existingAccess,
    );
    console.log(
      "filteredSupplierNamesListfilteredSupplierNamesList",
      filteredSupplierNamesList,
    );
    setSupplierNamesList(filteredSupplierNamesList);
  }, [existingAccess]);

  const getProjectEEAndSWMDetails = async ({ showToast }) => {
    console.log("props.data.access_type", props.data.access_type);
    const requestDetails = buildRequestDetails({
      accessType: props.data.access_type,
      data: props.data,
      showToast,
    });
    const resp = await dispatch(HandleApiActions(requestDetails));
    customConsole("-- resp----");
    customConsole(resp);
    if (resp.error || !resp.data) {
      setExistingAccess(EMPTY_ACCESS);
      props.handleClose();
    } else {
      const data = resp.data[0] || {};
      const resolvedZoneDetails = resolveZoneDetails(
        data?.zone_details,
        props.data?.zone_position,
      );
      const { ownerList, coOwnerList, editorList, viewerList } = buildUserLists(
        data,
        usersInSystem,
      );
      const accessDetails = buildAccessDetails(
        props.data.access_type,
        data,
        resolvedZoneDetails,
      );
      setExistingAccess({
        ...accessDetails,
        owner_details: ownerList,
        co_owner_details: coOwnerList,
        edit_user_details: editorList,
        view_user_details: viewerList,
      });
      let entityUsers = [
        ...ownerList,
        ...coOwnerList,
        ...editorList,
        ...viewerList,
      ];
      setEntityAllUsersList(entityUsers);
      console.log("---edit_user_details---");
    }
  };
  useEffect(() => {
    customConsole("-- ProjectAccessDrawer --");
    customConsole(props);
    getUsersInSystem();
  }, [props]);
  useEffect(() => {
    if (usersInSystem?.length) {
      customConsole("-- usersInSystem--");
      customConsole(usersInSystem);
      getProjectEEAndSWMDetails({ showToast: false });
    }
  }, [usersInSystem]);
  const MapUserDataInList = (details) => {
    return details.data.length && !Number.isNaN(details.data[0].role_id) ? (
      <Select
        variant={"standard"}
        sx={{ minWidth: "200px", width: "100%", gap: 1 }}
        defaultValue={details.data[0].email}
      >
        {details.data.map((data, idx) => {
          return (
            <MenuItem
              value={data?.email}
              key={idx}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                fontSize: "14px",
                color: "black",
              }}
            >
              {data?.fullname}
              <br />
              {data.email}
            </MenuItem>
          );
        })}
      </Select>
    ) : (
      <strong>-</strong>
    );
  };

  const renderAccessDetails = () => {
    switch (props.data.access_type) {
      case ACCESS_TYPES.PROJECT:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "green",
              }}
            >
              Project Details:
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Name: </span>
              {existingAccess?.project_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Architecture: </span>
              {existingAccess?.project_details?.architecture_name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Status: </span>
              {helper?.getObjectKeyByValue(
                project_status,
                existingAccess?.project_details?.status,
              ) || "-"}
            </Typography>
          </Box>
        );
      case ACCESS_TYPES.VARIANT:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "green",
              }}
            >
              Variant Details:
            </Typography>

            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Name: </span>
              {existingAccess?.variant_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Status: </span>
              {existingAccess?.variant_details?.vehicle_status || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Delivery Market: </span>
              {existingAccess?.variant_details?.delivery_market || "-"}
            </Typography>
          </Box>
        );
      case ACCESS_TYPES.EE:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              EE Component Details:
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Version: </span>
              {existingAccess?.ee_details?.version || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Supplier: </span>
              {existingAccess?.ee_details?.supplier_name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Description: </span>
              {existingAccess?.ee_details?.description || "-"}
            </Typography>
          </Box>
        );
      case ACCESS_TYPES.SWM:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Software Module Details:
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Name: </span>
              {existingAccess?.swm_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Version: </span>
              {existingAccess?.swm_details?.version || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Summary: </span>
              {existingAccess?.swm_details?.summary || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Description: </span>
              {existingAccess?.swm_details?.description || "-"}
            </Typography>
          </Box>
        );
      case ACCESS_TYPES.VEHICLE_SYSTEM:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Vehicle System Details:
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Name: </span>
              {existingAccess?.vehicle_system_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Assembly Number: </span>
              {existingAccess?.vehicle_system_details?.assembly_number || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Description: </span>
              {existingAccess?.vehicle_system_details?.description || "-"}
            </Typography>
          </Box>
        );
      default:
        return (
          <Box>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              Vehicle Component Details:
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Name: </span>
              {existingAccess?.vehicle_component_details?.name || "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Component Number: </span>
              {existingAccess?.vehicle_component_details?.component_number ||
                "-"}
            </Typography>
            <Typography variant="subtitle2" sx={styles.mdDetailsTxt}>
              <span style={styles.txtBold}>Description: </span>
              {existingAccess?.vehicle_component_details?.description || "-"}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Drawer open={props.open} anchor={"right"}>
        {existingAccess?.text1?.length ? (
          <Box
            sx={{
              minWidth: { xs: "300px", sm: "440px" },
              maxWidth: { xs: "300px", sm: "440px" },
              padding: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                marginTop: "10px",
                marginBottom: "20px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  padding: "10px",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: "bold", fontSize: "18px" }}>
                  {getAccessTitle(props.data.access_type)}
                </Typography>
                <IconButton onClick={props.handleClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {/* <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>
              {existingAccess.text1}
            </Typography>
            <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
              {existingAccess.text2}
            </Typography>
            {existingAccess.text3.length ? (
              <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                {existingAccess.text3}
              </Typography>
            ) : null} */}
              {/* Module details */}
              <Box sx={styles.subBox}>
                {renderAccessDetails()}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    mb: 1.4,
                  }}
                >
                  <Typography
                    sx={{
                      // fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    {" "}
                    Notifications:
                  </Typography>
                  <CustomButtonV2
                    size="small"
                    variant="contained"
                    btnName="Enabled"
                    handleOnClick={() => setEnableNotification(true)}
                    btnStyle={styles.customButton}
                    endIcon={enableNotification ? <CheckBoxIcon /> : null}
                  />
                  <CustomButtonV2
                    size="small"
                    variant="contained"
                    btnName="Disabled"
                    handleOnClick={() => setEnableNotification(false)}
                    btnStyle={styles.customButton}
                    endIcon={enableNotification ? null : <CheckBoxIcon />}
                  />
                </Box>
              </Box>
              <Divider />
            </Box>
            <Box>
              {/* Existing Users */}
              <Box sx={styles.subBox}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    // color: "green"
                  }}
                >
                  {" "}
                  Existing users:
                </Typography>
                {/* Owner */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      // fontWeight: "bold",
                      fontSize: "14px",
                      minWidth: "70px",
                    }}
                  >
                    Owner
                  </Typography>
                  <strong>:</strong>
                  <Typography sx={{ fontSize: "14px", color: "black" }}>
                    {existingAccess.owner_details[0]?.fullname} <br />{" "}
                    {existingAccess.owner_details[0]?.email}
                  </Typography>
                </Box>
                {/* Co-owners, editors and viewers */}
                {[
                  { key: "Co-Owners", value: existingAccess.co_owner_details },
                  { key: "Editors", value: existingAccess.edit_user_details },
                  { key: "Viewers", value: existingAccess.view_user_details },
                ].map((user, idx) => {
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 2,
                        alignItems: "center",
                        marginTop: "10px",
                      }}
                    >
                      <Typography
                        sx={{
                          // fontWeight: "bold",
                          fontSize: "14px",
                          minWidth: "70px",
                        }}
                      >
                        {user.key}
                      </Typography>
                      <strong>:</strong>
                      <MapUserDataInList data={user.value} />
                    </Box>
                  );
                })}
              </Box>
              <Box
                sx={{
                  ...styles.subBox,
                  marginTop: "20px",
                  maxHeight: "50vH",
                }}
              >
                <CustomButtonV2
                  btnName="Manage Access"
                  handleOnClick={() => {
                    props.onManageAccess();
                    setShowGrant(true);
                  }}
                />
              </Box>
            </Box>
          </Box>
        ) : null}
      </Drawer>
      <GrantAccessDrawer
        supplierNamesList={supplierNamesList}
        open={showGrant}
        onClose={() => setShowGrant(false)}
        entityAllUsersList={entityAllUsersList}
        context={props.data}
      />
    </>
  );
}

AccessOverviewDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onManageAccess: PropTypes.func.isRequired,
  data: PropTypes.shape({
    access_type: PropTypes.string.isRequired,
    project_id: PropTypes.any,
    variant_id: PropTypes.any,
    ee_id: PropTypes.any,
    swm_id: PropTypes.any,
    vs_id: PropTypes.any,
    vc_id: PropTypes.any,
    zone_position: PropTypes.any,
  }).isRequired,
};

export default AccessOverviewDrawer;

const styles = {
  addedTextHeader: { fontWeight: "bold", fontSize: "14px" },
  userCancelBtn: {
    fontWeight: "bold",
    backgroundColor: "red",
    color: "white",
    textAlign: "center",
    height: "20px",
    width: "20px",
    fontSize: "12px",
    cursor: "pointer",
    marginLeft: "20px",
    "&:hover": {
      color: "black",
      backgroundColor: "red",
    },
  },
  closeBtn: {
    fontWeight: "bold",
    backgroundColor: "red",
    color: "white",
    textAlign: "center",
    height: "25px",
    width: "25px",
    fontSize: "18px",
    cursor: "pointer",
    marginLeft: "20px",
    "&:hover": {
      color: "black",
      backgroundColor: "red",
    },
  },
  subBox: {
    display: "flex",
    flexDirection: "column",
    borderRadius: "0",
    border: "2px solid green",
    padding: "10px",
    overflow: "auto",
  },
  addedUserTextHeader: {
    // fontWeight: "bold",
    fontSize: "14px",
    marginTop: "10px",
    marginBottom: "10px",
  },
  addedUserCloseBtn: {
    fontWeight: "bold",
    color: "white",
    marginLeft: "15px",
    backgroundColor: "red",
    borderRadius: "50%",
    padding: "2px",
    "&:hover": {
      backgroundColor: "black",
      color: "red",
    },
    cursor: "pointer",
  },
  addedUserText: {
    border: "1px solid black",
    borderRadius: "5px",
    fontWeight: "bold",
    fontSize: "12px",
    padding: "10px",
  },
  addedUsersbox: {
    display: "flex",
    flexWrap: "wrap",
    // maxHeight: "150px",
    overflow: "auto",
    gap: "10px",
  },
  commonButtonStyle: {
    backgroundImage:
      "linear-gradient( to bottom, rgb(7, 5, 69), rgb(103, 86, 133))",
    maxHeight: "30px",
    width: "fit-content",
    borderRadius: "20px",
    color: "white",
    "&:hover": {
      backgroundImage: "linear-gradient(#6b8df2,#021636)",
    },
  },
  // txtBold: {
  //   fontWeight: "bold",
  // },
  mdDetailsTxt: {
    my: 1.4,
  },
};
